import React, { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Separator } from "./ui/separator";
import padlockIcon from "../assets/icons/padlock.svg";
import logoSmall from "../assets/images/logoSmallGrey.svg";
import { ArrowLeft, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "./ui/dialog";
import { ChevronDown, X, Wallet } from "lucide-react";
import AccountSelect from "./AccountSelect";
import coin from "../assets/images/placeholder/crypto-colour.png";
import AccountSelectSecondary from "./AccountSelectSecondary";
import CurrencyFormatter from "../services/currencyFormatterService";
import { formatFiatWithTwoDecimals } from "../utils/numberFormatter";

export default function TransferFunds({
  childAmount,
  setChildAmount,
  setTransfer,
  onContinue,
  accounts,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedAccountFrom, setSelectedAccountFrom] = useState(null);
  const [open, setOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [holdStep, setHoldStep] = useState(false);
  const [flag, setFlag] = useState(false);
  const [exceedFlag, setExceedFlag] = useState(false);
  const [formFlag, setFormFlag] = useState(true);
  const [loadingForm, setLoadingForm] = useState(false);
  const assets = selectedAccountFrom?.balance?.assetBalances;
  const filteredAssets = useMemo(() => {
    const combinedList = [
      ...Object.values(assets?.crypto || {}),
      ...Object.values(assets?.fiat || {}),
    ];
    return combinedList?.filter(
      (item) =>
        item?.asset?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item?.assetName?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, assets]);
  const updatedAccounts = useMemo(
    () =>
      accounts.map((account) => ({
        ...account,
        Icon: Wallet,
      })),
    [accounts]
  );
  const maxAmount = () => {
    if (!selectedAsset) return;
    if (selectedAsset) {
      setChildAmount(selectedAsset?.availableAmount);
      onAmountChange({ target: { value: selectedAsset?.availableAmount } });
    }
  };
  const onAmountChange = (e) => {
    const value = e.target.value;
    const numberRegex = /^\d*\.?\d*$/;
    const availableAmount = parseFloat(selectedAsset?.availableAmount);
    if (numberRegex.test(value)) {
      setChildAmount(value);
      if (value === 0) {
        setFlag(true);
        setHoldStep(true);
        setChildAmount(value);
      }
      if (value > 0) {
        setChildAmount(value);
        setFlag(false);
        setHoldStep(false);
      }
      if (value > availableAmount) {
        setChildAmount(value);
        setExceedFlag(true);
        setHoldStep(true);
      }
      if (value <= availableAmount) {
        setChildAmount(value);
        setExceedFlag(false);
        setHoldStep(false);
      }
    }
  };
  const handleSelect = (asset) => {
    if (asset?.asset === selectedAsset?.asset) {
      setSelectedAsset(asset);
      setOpen(false);
      return;
    }
    if (asset?.asset !== selectedAsset?.asset) {
      setSelectedAsset(asset);
      setOpen(false);
    }
  };
  useEffect(() => {
    setLoadingForm(true);
    const completeForm = () => {
      if (
        selectedAccount &&
        selectedAccountFrom &&
        selectedAsset &&
        childAmount
      ) {
        setFormFlag(false);
        setLoadingForm(false);
      }
      if (!selectedAccount) {
        setFormFlag(true);
        setLoadingForm(false);
      }
      if (!selectedAccountFrom) {
        setFormFlag(true);
        setLoadingForm(false);
      }
      if (!selectedAsset) {
        setFormFlag(true);
        setLoadingForm(false);
      }
      if (!childAmount) {
        setFormFlag(true);
        setLoadingForm(false);
      }
    };
    completeForm();
  }, [selectedAccount, selectedAccountFrom, selectedAsset, childAmount]);
  const transferObjectPartial = {
    accountIdTo: selectedAccount?._id,
    accountIdToName: selectedAccount?.accountName,
    accountIdFrom: selectedAccountFrom?._id,
    accountIdFromName: selectedAccountFrom?.accountName,
    amount: childAmount,
    assetId: selectedAsset?.assetId,
    asset: selectedAsset?.asset,
    type: selectedAsset?.type,
  };
  const formatter = new CurrencyFormatter();
  return (
    <>
      <div className="h-14 bg-gradient-to-r from-blue-600 to-purple-600 flex items-center pl-20 text-white">
        <div
          className="mr-5 border-rounded rounded-full p-1 border"
          onClick={() => {
            setTransfer(false);
          }}
        >
          <ArrowLeft size={14} />
        </div>
        <p className="text-sm font-medium">
          Settings / Sub accounts / Transfer funds
        </p>
      </div>
      <div className="flex justify-center bg-gray-100 mt-12 px-4 md:px-16 lg:px-32 min-h-screen">
        <div className="max-w-3xl w-full">
          <Card className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <CardHeader className="px-9 pt-6 pb-4">
              <CardTitle className="text-2xl font-semibold text-[#6D6584] text-[20px]">
                <div className="flex items-center">
                  <div>Transfer funds</div>
                </div>
              </CardTitle>
            </CardHeader>

            <CardContent className="px-5 pt-2  pb-6">
              <div className="grid grid-cols-2 mb-6">
                <div className="relative w-full px-4">
                  <p className="text-[14px] text-[#6D6584] font-normal mb-2">
                    Transfer from
                  </p>
                  <AccountSelectSecondary
                    selectedAccountFrom={selectedAccountFrom}
                    selectedAccount={selectedAccount}
                    onSelect={(item) => {
                      setSelectedAccountFrom(item);
                    }}
                    list={updatedAccounts}
                  />
                </div>
                <div className="relative w-full px-4">
                  <p className="text-[14px] text-[#6D6584] font-normal mb-2">
                    Available balance
                  </p>
                  <div className="p-5 rounded-2xl bg-gray-50 flex-1 flex items-center">
                    <div className="text-xl font-semibold text-gray-600">
                      {selectedAccountFrom?.balance?.portfolioEstimate}
                    </div>
                  </div>
                </div>
              </div>
              <div className="columns-1 mb-6">
                <div className="grid grid-cols-4 gap-4 grid-rows-1 py-8 px-3">
                  <div className="space-y-2 col-span-1">
                    <label className="text-gray-600 text-sm">
                      Select asset
                    </label>
                    <Dialog open={open} onOpenChange={setOpen}>
                      <DialogTrigger asChild>
                        <Button
                          disabled={false}
                          variant="outline"
                          className="w-full flex items-center justify-between py-8 rounded-2xl hover:border-[#54279c] transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full flex items-center justify-center">
                              {selectedAsset ? (
                                <img
                                  src={selectedAsset?.assetIcon}
                                  alt={selectedAsset?.assetName}
                                  width={32}
                                  height={32}
                                  className="text-white"
                                />
                              ) : (
                                <img
                                  src={coin}
                                  alt={"Default Asset"}
                                  width={32}
                                  height={32}
                                  className="text-white"
                                />
                              )}
                            </div>
                            <div className="text-left">
                              <div className="font-semibold">
                                {selectedAsset?.asset.toUpperCase() ||
                                  "Select..."}
                              </div>
                            </div>
                          </div>
                          <ChevronDown className="text-gray-400" />
                        </Button>
                      </DialogTrigger>

                      <DialogContent className="max-w-sm rounded-2xl p-6">
                        <DialogClose asChild>
                          <Button
                            variant="ghost"
                            onClick={() => setOpen(false)}
                            className="w-10 h-10 absolute top-4 right-4 text-gray-400 hover:text-gray-600 rounded-full p-0 flex items-center justify-center border border-gray-200 hover:bg-gray-100"
                          >
                            <X size={18} className="text-gray-700" />
                          </Button>
                        </DialogClose>

                        <DialogHeader>
                          <DialogTitle className="text-lg font-semibold text-gray-800">
                            Select an Asset
                          </DialogTitle>
                        </DialogHeader>
                        <div className="mt-4">
                          <input
                            type="text"
                            placeholder="Search addresses..."
                            className="w-full border rounded-lg px-3 py-2"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </div>
                        <div className="mt-4 flex flex-col space-y-3 max-h-72 overflow-y-auto">
                          {filteredAssets.map((asset) => {
                            return (
                              <button
                                key={asset?.assetId}
                                onClick={() => {
                                  handleSelect(asset);
                                }}
                                className="flex items-center gap-3 justify-between px-3 py-3 border rounded-lg border-transparent bg-gray-50 hover:border-rose-200"
                              >
                                <div className="flex items-center gap-3">
                                  <img
                                    src={asset?.assetIcon}
                                    alt={asset?.assetName}
                                    className="w-6 h-6"
                                  />
                                  <div className="flex flex-col text-left">
                                    <span>{asset?.assetName}</span>
                                    <span className="text-xs text-gray-500">
                                      {asset?.asset?.toUpperCase()}
                                    </span>
                                  </div>
                                </div>
                                <div className="flex flex-col text-right">
                                  <span className="font-bold">
                                    {formatter.formatCrypto(
                                      asset?.availableAmount,
                                      asset?.asset?.toUpperCase()
                                    )}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {asset?.valueInLocalCurrency
                                      ? formatFiatWithTwoDecimals(
                                          asset.valueInLocalCurrency
                                        )
                                      : "R 0.00"}
                                  </span>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                  <div className="space-y-2 col-span-3">
                    <label className="text-gray-600 text-sm">
                      Available balance
                    </label>
                    <div className="p-5 rounded-2xl bg-gray-50 flex-1 flex items-center">
                      <div className="text-xl font-semibold text-gray-600">
                        {selectedAsset?.forattedAvailableAmount}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="columns-1 mb-6">
                  <div className="space-y-2 col-span-3 px-2">
                    <label className="text-gray-600 text-sm">
                      {"Enter Amount"}
                    </label>
                    <div
                      className={`flex items-center gap-4 rounded-2xl p-3 border ${
                        flag || exceedFlag ? "border-2 border-rose-500 " : " "
                      }`}
                    >
                      <input
                        type="text"
                        value={childAmount}
                        onChange={onAmountChange}
                        className={`flex-1 text-base md:text-sm font-semibold bg-transparent outline-none 
    focus-visible:ring-0 focus-visible:outline-none transition-colors duration-200 ease-in-out
    ${flag || exceedFlag ? "text-rose-500" : "text-[#54279c]"}`}
                        placeholder="Enter amount"
                      />

                      <div className="flex items-center gap-2">
                        <Button
                          onClick={maxAmount}
                          variant="outline"
                          className="rounded-full"
                        >
                          Max
                        </Button>
                      </div>
                    </div>
                    {flag && (
                      <p className="mt-1 text-sm text-rose-500">
                        This amount is less than the allowed amount
                      </p>
                    )}
                    {exceedFlag && (
                      <p className="mt-1 text-sm text-rose-500">
                        This amount is more than your available amount{" "}
                        {formatter.formatCrypto(
                          selectedAsset?.availableAmount,
                          selectedAsset?.asset?.toUpperCase()
                        )}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 mb-6">
                <div className="relative w-full px-4">
                  <p className="text-[14px] text-[#6D6584] font-normal mb-2">
                    Transfer to
                  </p>
                  <AccountSelect
                    selectedAccount={selectedAccount}
                    selectedAccountFrom={selectedAccountFrom}
                    onSelect={(item) => {
                      setSelectedAccount(item);
                    }}
                    list={updatedAccounts}
                  />
                </div>

                <div className="relative w-full px-4">
                  <p className="text-[14px] text-[#6D6584] font-normal mb-2">
                    Available balance
                  </p>
                  <div className="p-5 rounded-2xl bg-gray-50 flex-1 flex items-center">
                    <div className="text-xl font-semibold text-gray-600">
                      {selectedAccount?.balance?.portfolioEstimate}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-center py-3">
                <Button
                  className="bg-gradient-to-tr from-purple-500 to-rose-500 text-white py-2 rounded-full w-[40%]"
                  disabled={exceedFlag || flag || holdStep || formFlag}
                  onClick={() => {
                    onContinue?.(transferObjectPartial);
                  }}
                >
                  {loadingForm && <Loader2 className="animate-spin w-4 h-4" />}
                  Continue
                </Button>
              </div>
            </CardContent>
            <Separator />
            <div className="py-4 text-[14px] text-[#6D6584] font-normal">
              <div className="flex justify-center items-center">
                <img src={padlockIcon} alt="Padlock icon" className="mr-1" />
                <p>Secured by</p>
                <img src={logoSmall} alt="Logo" className="ml-1" />
              </div>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}
